import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowDownToLine, Menu, X } from "lucide-react";

import { mock } from "@/mock";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const NavItem = ({ to, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      cn(
        "rounded-xl px-3 py-2 text-[13px] font-medium transition-colors",
        isActive
          ? "bg-white/10 text-white"
          : "text-white/60 hover:bg-white/[0.06] hover:text-white"
      )
    }
    style={{ transition: "background-color 180ms ease, color 180ms ease" }}
  >
    {label}
  </NavLink>
);

export const SiteLayout = ({ children }) => {
  const location = useLocation();
  const nav = useMemo(() => mock.nav, []);
  const [scrolled, setScrolled] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const onDownload = () => setDownloadOpen(true);
    window.addEventListener("smaughk:download", onDownload);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("smaughk:download", onDownload);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#070708] text-white">
      {/* background grid */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.22]"
      >
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.06)_1px,transparent_1px)] bg-[size:26px_26px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(16,185,129,0.16),transparent_40%),radial-gradient(circle_at_20%_20%,rgba(139,92,246,0.12),transparent_45%)]" />
      </div>

      <header
        className={cn(
          "sticky top-0 z-40 border-b border-white/10",
          scrolled
            ? "bg-black/40 backdrop-blur-2xl"
            : "bg-black/20 backdrop-blur-xl"
        )}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4 sm:px-10">
          <div className="flex items-center gap-4">
            <Link to="/" className="group flex items-center gap-2">
              <div className="relative">
                <div className="absolute -inset-2 rounded-2xl bg-emerald-400/10 blur-xl opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="relative flex h-9 w-9 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                  <img
                    src={require("@/assets/smaughk-logo-white.png")}
                    alt="Smaughk logo"
                    className="h-6 w-6 select-none"
                    draggable={false}
                  />
                </div>
              </div>
              <div>
                <div className="text-[13px] font-semibold tracking-[-0.02em] text-white">
                  Smaughk
                </div>
                
              </div>
            </Link>

            <nav className="hidden items-center gap-1 lg:flex">
              {nav.map((n) => (
                <NavItem key={n.to} to={n.to} label={n.label} />
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <Dialog open={downloadOpen} onOpenChange={setDownloadOpen}>
              <DialogTrigger asChild>
                <Button className="hidden h-9 rounded-xl px-4 lg:inline-flex">
                  <ArrowDownToLine className="mr-2 h-4 w-4" /> Download
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-2xl border-white/10 bg-black/60 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_30px_120px_rgba(0,0,0,0.65)] backdrop-blur-2xl">
                <DialogHeader>
                  <DialogTitle className="text-3xl tracking-[-0.02em]">
                    Shipping soon
                  </DialogTitle>
                  <DialogDescription className="text-[15px] leading-7 text-white/60">
                    We’re polishing the installer and the first public release.
                  </DialogDescription>
                </DialogHeader>

                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <div className="text-[13px] font-medium text-white/80">
                    What you’ll get on day one
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    {[
                      "Local mock services",
                      "Shareable environments",
                      "Team collaboration",
                    ].map((t) => (
                      <div
                        key={t}
                        className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-[13px] text-white/65"
                      >
                        {t}
                      </div>
                    ))}
                  </div>
                </div>

                <DialogFooter className="mt-2">
                  <Button
                    variant="secondary"
                    onClick={() => setDownloadOpen(false)}
                    className="h-10 rounded-xl border border-white/10 bg-white/5 px-4 text-white/85 hover:bg-white/10"
                  >
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="secondary"
                    className="h-9 rounded-xl border border-white/10 bg-white/5 px-3 text-white/80 hover:bg-white/10"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="w-[340px] border-white/10 bg-black/60 text-white backdrop-blur-2xl"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[13px] font-semibold">Navigation</div>
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        className="h-9 rounded-xl border border-white/10 bg-white/[0.03] text-white/80 hover:bg-white/[0.06]"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </SheetClose>
                  </div>

                  <Separator className="my-4 bg-white/10" />

                  <div className="grid gap-1">
                    {nav.map((n) => (
                      <SheetClose key={n.to} asChild>
                        <div>
                          <NavItem to={n.to} label={n.label} />
                        </div>
                      </SheetClose>
                    ))}
                  </div>

                  <Separator className="my-4 bg-white/10" />

                  <Dialog open={downloadOpen} onOpenChange={setDownloadOpen}>
                    <DialogTrigger asChild>
                      <SheetClose asChild>
                        <Button className="h-10 rounded-xl">
                          <ArrowDownToLine className="mr-2 h-4 w-4" /> Download
                        </Button>
                      </SheetClose>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl border-white/10 bg-black/60 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_30px_120px_rgba(0,0,0,0.65)] backdrop-blur-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-3xl tracking-[-0.02em]">
                          Shipping soon
                        </DialogTitle>
                        <DialogDescription className="text-[15px] leading-7 text-white/60">
                          We’re polishing the installer and the first public
                          release.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button
                          variant="secondary"
                          onClick={() => setDownloadOpen(false)}
                          className="h-10 rounded-xl border border-white/10 bg-white/5 px-4 text-white/85 hover:bg-white/10"
                        >
                          Close
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 border-t border-white/10">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 py-10 sm:px-10 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
                <img
                  src={require("@/assets/smaughk-logo-white.png")}
                  alt="Smaughk logo"
                  className="h-6 w-6 select-none"
                  draggable={false}
                />
              </div>
              <div>
                <div className="text-[13px] font-semibold">Smaughk</div>
                <div className="text-[12px] text-white/50">
                  Code Your Mocks, Share Your Vision
                </div>
              </div>
            </div>

            

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {[
                "Zero-Trust Overlay Network",
                "Instant HTTPS Tunnels",
                "Infrastructure as Code",
              ].map((t) => (
                <Badge
                  key={t}
                  className="border-white/10 bg-white/[0.03] text-white/65"
                >
                  {t}
                </Badge>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[12px] font-medium uppercase tracking-[0.12em] text-white/40">
                Product
              </div>
              <div className="mt-3 grid gap-2">
                {nav.slice(0, 3).map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    className="text-[13px] text-white/55 hover:text-white"
                    style={{ transition: "color 180ms ease" }}
                  >
                    {n.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[12px] font-medium uppercase tracking-[0.12em] text-white/40">
                Resources
              </div>
              <div className="mt-3 grid gap-2">
                {nav.slice(3).map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    className="text-[13px] text-white/55 hover:text-white"
                    style={{ transition: "color 180ms ease" }}
                  >
                    {n.label}
                  </Link>
                ))}
                <a
                  href="https://emergent.sh"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[13px] text-white/55 hover:text-white"
                  style={{ transition: "color 180ms ease" }}
                >
                  Emergent
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-6 py-5 text-[12px] text-white/45 sm:px-10">
            <div>© {new Date().getFullYear()} Smaughk. All rights reserved.</div>
            <div className="flex items-center gap-3">
              <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
                {location.pathname}
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
